import { ComponentProps, useEffect, useRef } from "react";
import { ScrollArea, ScrollBar } from "./scroll-area";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cn } from "@/lib/utils";
import { ChevronRightIcon } from "lucide-react";

export const GuidedScrollArea = ({
  children,
  ref,
  className,
  ...props
}: Omit<ComponentProps<typeof ScrollArea>, "asChild" | "onScroll">) => {
  const gradientRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const viewPortRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!viewPortRef.current) return;
    const { clientWidth } = viewPortRef.current;
    viewPortRef.current.scrollBy({
      left: clientWidth * 0.7,
      behavior: "smooth",
    });
  };

  const handleScroll = () => {
    if (!gradientRef.current || !buttonRef.current || !viewPortRef.current)
      return;

    const target = viewPortRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = target;
    const shouldShow = scrollLeft < scrollWidth - clientWidth - 40;
    gradientRef.current.style.opacity = shouldShow ? "1" : "0";
    buttonRef.current.style.opacity = shouldShow ? "1" : "0";
  };

  useEffect(() => handleScroll(), [gradientRef, buttonRef, viewPortRef]);

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className="h-full w-full rounded-[inherit]"
        ref={viewPortRef}
        onScroll={handleScroll}
      >
        {children}
        <div
          ref={gradientRef}
          className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-background to-transparent pointer-events-none transition-opacity duration-200"
        >
          <button
            ref={buttonRef}
            onClick={handleClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow-sm transition-opacity duration-200 pointer-events-auto cursor-pointer hover:bg-gray-50"
          >
            <ChevronRightIcon className="w-4 h-4 text-primary" />
          </button>
        </div>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
};
